import React, { useEffect, useState, useCallback, useMemo } from 'react';
import createContextHook from '@nkzw/create-context-hook';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { queryClient } from '@/lib/queryClient';
import { API_URL } from '@/lib/api';
import { logEvent } from '@/lib/analytics';

export interface User {
  id: string;
  email: string;
  name: string;
  image?: string;
}

export interface Session {
  user: User;
  accessToken: string;
  tokenType: string;
}

/**
 * Decode the payload of a JWT without a library.
 * Returns null if the token is malformed or not a valid JWT.
 */
function decodeJWTPayload(token: string): Record<string, any> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // base64url → base64
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    // Pad to multiple of 4
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Check whether a JWT access token is expired.
 * Returns true if the token is missing, malformed, or its `exp` claim is in the past.
 */
function isTokenExpired(token: string | undefined): boolean {
  if (!token) return true;
  const payload = decodeJWTPayload(token);
  if (!payload || typeof payload.exp !== 'number') return false; // no exp claim → can't tell, assume valid
  // Allow 30 seconds of clock skew
  return payload.exp * 1000 < Date.now() - 30_000;
}

export const [AuthContext, useAuth] = createContextHook(() => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // ─── Global 401 interceptor ─────────────────────────────────────
  // Any fetch that returns 401 should sign the user out.
  useEffect(() => {
    const originalFetch = globalThis.fetch;
    let mounted = true;

    globalThis.fetch = async function patchedFetch(input: RequestInfo | URL, init?: RequestInit) {
      const response = await originalFetch(input, init);

      if (response.status === 401 && mounted) {
        // Only intercept 401s on *our* API — not third-party URLs
        const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request).url;
        if (url.startsWith(API_URL)) {
          console.warn('401 response from API — signing out');
          // Sign out asynchronously — don't block the fetch return
          handleSignOut();
        }
      }

      return response;
    };

    return () => {
      mounted = false;
      globalThis.fetch = originalFetch;
    };
  }, []);

  // ─── Load session from storage + validate token ──────────────────
  useEffect(() => {
    const loadSession = async () => {
      try {
        const savedSession = await AsyncStorage.getItem('session');
        if (savedSession) {
          const parsedSession = JSON.parse(savedSession);

          // Check if the token is expired before accepting it
          if (isTokenExpired(parsedSession.accessToken)) {
            console.warn('Stored token is expired — clearing session');
            await AsyncStorage.removeItem('session');
            setSession(null);
            setUser(null);
          } else {
            setSession(parsedSession);
            setUser(parsedSession.user);
          }
        }
      } catch (error) {
        console.error('Error loading session:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSession();
  }, []);

  const signInWithToken = useCallback(async (tokenResponse: { accessToken: string; tokenType?: string; user?: User }) => {
    // Accept token response from webauthn auth verify endpoint and persist session
    const sessionObj: Session = {
      accessToken: tokenResponse.accessToken,
      tokenType: tokenResponse.tokenType || 'bearer',
      user: (tokenResponse.user as User) || (user as User),
    } as Session;
    setSession(sessionObj);
    setUser(sessionObj.user);
    await AsyncStorage.setItem('session', JSON.stringify(sessionObj));
    return sessionObj;
  }, [user]);

  // Sign out + clear ALL cached user data. Without clearing the query cache
  // (and the persisted selected-dog), the next account sees the previous
  // user's dogs (bug: "Old Profile Reloaded").
  const handleSignOut = useCallback(async () => {
    setSession(null);
    setUser(null);
    try {
      await AsyncStorage.removeItem('session');
      await AsyncStorage.removeItem('selectedDogId');
    } catch (error) {
      console.error('Error clearing auth storage:', error);
    }
    queryClient.clear();
  }, []);

  const signOut = useCallback(async () => {
    console.log('Signing out user');
    logEvent('sign_out').catch(() => {});
    await handleSignOut();
  }, [handleSignOut]);

  return useMemo(() => ({
    session,
    user,
    isLoading,
    signInWithToken,
    signOut,
    isAuthenticated: !!session,
  }), [session, user, isLoading, signInWithToken, signOut]);
});